import { t, type TranslationKey } from './i18n/index.js';

// 화면 상수 (동적으로 가져옴)
export const getScreenSize = () => ({
    WIDTH: window.innerWidth,
    HEIGHT: window.innerHeight,
});

// 무기 정의 (레벨별로 완전히 다른 무기)
export interface Weapon {
    nameKey: TranslationKey;
    image: string | null;
}

// 레벨별 무기 목록 (0강 ~ 34강+) — 이름은 i18n 키로 저장, 표시 시 t()로 해석
export const WEAPONS: Weapon[] = [
    // 0: 시작 상태 (이미지 없음)
    { nameKey: 'weapon_0', image: null },
    // 1-10: 초반 무기
    { nameKey: 'weapon_1', image: './swords/1.png' },
    { nameKey: 'weapon_2', image: './swords/2.png' },
    { nameKey: 'weapon_3', image: './swords/3.png' },
    { nameKey: 'weapon_4', image: './swords/4.png' },
    { nameKey: 'weapon_5', image: './swords/5.png' },
    { nameKey: 'weapon_6', image: './swords/6.png' },
    { nameKey: 'weapon_7', image: './swords/7.png' },
    { nameKey: 'weapon_8', image: './swords/8.png' },
    { nameKey: 'weapon_9', image: './swords/9.png' },
    { nameKey: 'weapon_10', image: './swords/10.png' },
    // 11-20: 중반 무기
    { nameKey: 'weapon_11', image: './swords/11.png' },
    { nameKey: 'weapon_12', image: './swords/12.png' },
    { nameKey: 'weapon_13', image: './swords/13.png' },
    { nameKey: 'weapon_14', image: './swords/14.png' },
    { nameKey: 'weapon_15', image: './swords/15.png' },
    { nameKey: 'weapon_16', image: './swords/16.png' },
    { nameKey: 'weapon_17', image: './swords/17.png' },
    { nameKey: 'weapon_18', image: './swords/18.png' },
    { nameKey: 'weapon_19', image: './swords/19.png' },
    { nameKey: 'weapon_20', image: './swords/20.png' },
    // 21-30: 후반 무기
    { nameKey: 'weapon_21', image: './swords/21.png' },
    { nameKey: 'weapon_22', image: './swords/22.png' },
    { nameKey: 'weapon_23', image: './swords/23.png' },
    { nameKey: 'weapon_24', image: './swords/24.png' },
    { nameKey: 'weapon_25', image: './swords/25.png' },
    { nameKey: 'weapon_26', image: './swords/26.png' },
    { nameKey: 'weapon_27', image: './swords/27.png' },
    { nameKey: 'weapon_28', image: './swords/28.png' },
    { nameKey: 'weapon_29', image: './swords/29.png' },
    { nameKey: 'weapon_30', image: './swords/30.png' },
    // 31-34: 전설 무기
    { nameKey: 'weapon_31', image: './swords/31.png' },
    { nameKey: 'weapon_32', image: './swords/32.png' },
    { nameKey: 'weapon_33', image: './swords/33.png' },
    { nameKey: 'weapon_34', image: './swords/34.png' },
];

// 레벨에 해당하는 무기 가져오기
export function getWeaponByLevel(level: number): Weapon {
    if (level >= WEAPONS.length) {
        return WEAPONS[WEAPONS.length - 1];
    }
    return WEAPONS[level];
}

export function weaponName(w: Weapon): string {
    return t(w.nameKey);
}

export function getWeaponName(level: number): string {
    return t(getWeaponByLevel(level).nameKey);
}

// 강화 확률 (0강 → 1강, 1강 → 2강, ...)
// 인덱스 = 현재 강화 수치
// 자연스러운 난이도 곡선 - 같은 확률이 연속될 수도 있음
// 최대 레벨
export const MAX_LEVEL = 34;

export const ENHANCE_RATES: number[] = [
    0.98,  // 0 → 1: 98%
    0.95,  // 1 → 2: 95%
    0.93,  // 2 → 3: 93%
    0.90,  // 3 → 4: 90%
    0.86,  // 4 → 5: 86%
    0.81,  // 5 → 6: 81%
    0.75,  // 6 → 7: 75%
    0.70,  // 7 → 8: 70%
    0.66,  // 8 → 9: 66%
    0.62,  // 9 → 10: 62%
    0.58,  // 10 → 11: 58%
    0.54,  // 11 → 12: 54%
    0.50,  // 12 → 13: 50%
    0.47,  // 13 → 14: 47%
    0.44,  // 14 → 15: 44%
    0.41,  // 15 → 16: 41%
    0.38,  // 16 → 17: 38%
    0.35,  // 17 → 18: 35%
    0.32,  // 18 → 19: 32%
    0.29,  // 19 → 20: 29%
    0.26,  // 20 → 21: 26%
    0.24,  // 21 → 22: 24%
    0.22,  // 22 → 23: 22%
    0.20,  // 23 → 24: 20%
    0.18,  // 24 → 25: 18%
    0.16,  // 25 → 26: 16%
    0.15,  // 26 → 27: 15%
    0.14,  // 27 → 28: 14%
    0.13,  // 28 → 29: 13%
    0.12,  // 29 → 30: 12%
    0.11,  // 30 → 31: 11%
    0.10,  // 31 → 32: 10%
    0.09,  // 32 → 33: 9%
    0.08,  // 33 → 34: 8%
];

// 강화 비용 계산
// 중·고레벨 배율 완화 — "한 무기 팔면 다음 구간 여러 번 시도 가능" 느낌
export function getEnhanceCost(level: number): number {
    if (level <= 10) {
        // 초반: 완만한 증가
        return Math.floor(100 * Math.pow(1.4, level));
    } else if (level <= 20) {
        // 중반: 조금 더 가파르게
        const base = 100 * Math.pow(1.4, 10);
        return Math.floor(base * Math.pow(1.5, level - 10));
    } else if (level <= 30) {
        // 후반: 1.6 → 1.45로 완화
        const base10 = 100 * Math.pow(1.4, 10);
        const base20 = base10 * Math.pow(1.5, 10);
        return Math.floor(base20 * Math.pow(1.45, level - 20));
    } else {
        // 신화 구간: 1.8 → 1.4로 대폭 완화 (막힘 방지)
        const base10 = 100 * Math.pow(1.4, 10);
        const base20 = base10 * Math.pow(1.5, 10);
        const base30 = base20 * Math.pow(1.45, 10);
        return Math.floor(base30 * Math.pow(1.4, level - 30));
    }
}
// 레벨별 강화 비용 (조정 후):
// Lv.0→1: 100G, Lv.5→6: 537G, Lv.10→11: 2,893G
// Lv.15→16: 22,000G, Lv.20→21: 167,000G
// Lv.25→26: 1,069,000G, Lv.30→31: 6,853,000G
// Lv.33→34: 18,804,000G (기존 1억에서 1,880만으로 대폭 완화)

// 무기 판매가 계산 — 비용 완화에 맞춰 일부 구간 상향
export function getSellPrice(level: number): number {
    if (level <= 1) return 0;

    if (level <= 5) {
        // 2-5강: 초반
        return level * 2000; // 4,000G ~ 10,000G
    } else if (level <= 10) {
        // 6-10강: 초중반
        return 15000 + (level - 5) * 10000; // 25,000G ~ 65,000G
    } else if (level <= 15) {
        // 11-15강: 중반
        return 80000 + (level - 10) * 50000; // 130,000G ~ 330,000G
    } else if (level <= 20) {
        // 16-20강: 중후반
        return 400000 + (level - 15) * 200000; // 600,000G ~ 1,400,000G
    } else if (level <= 25) {
        // 21-25강: 고난이도
        return 2000000 + (level - 20) * 1000000; // 3,000,000G ~ 7,000,000G
    } else if (level <= 30) {
        // 26-30강: 전설 (5M → 6M으로 소폭 상향)
        return 10000000 + (level - 25) * 6000000; // 16,000,000G ~ 40,000,000G
    } else {
        // 31강+: 신화 (비용 완화에 맞춰 25M 스텝)
        return 50000000 + (level - 30) * 25000000; // 75,000,000G ~ 150,000,000G
    }
}
// 레벨별 판매가 (조정 후):
// Lv.2-5: 4,000G ~ 10,000G
// Lv.6-10: 25,000G ~ 65,000G
// Lv.11-15: 13만G ~ 33만G
// Lv.16-20: 60만G ~ 140만G
// Lv.21-25: 300만G ~ 700만G
// Lv.26-30: 1,600만G ~ 4,000만G
// Lv.31+: 7,500만G ~ 1.5억G

// 강화 확률 가져오기
export function getEnhanceRate(level: number): number {
    if (level >= ENHANCE_RATES.length) {
        return 0.05; // 최소 5%
    }
    return ENHANCE_RATES[level];
}

// 티어별 방지권 시스템
export interface ProtectionScroll {
    nameKey: TranslationKey;
    minLevel: number;  // 사용 가능 최소 레벨
    maxLevel: number;  // 사용 가능 최대 레벨
    price: number;
}

export const PROTECTION_SCROLLS: ProtectionScroll[] = [
    { nameKey: 'scroll_basic', minLevel: 1, maxLevel: 9, price: 5000 },
    { nameKey: 'scroll_mid', minLevel: 10, maxLevel: 19, price: 50000 },
    { nameKey: 'scroll_hi', minLevel: 20, maxLevel: 24, price: 500000 },
    { nameKey: 'scroll_ultra', minLevel: 25, maxLevel: 34, price: 5000000 },
];

export function scrollName(s: ProtectionScroll): string {
    return t(s.nameKey);
}

// 레벨에 맞는 방지권 티어 인덱스 반환
export function getProtectionTierForLevel(level: number): number {
    for (let i = 0; i < PROTECTION_SCROLLS.length; i++) {
        const scroll = PROTECTION_SCROLLS[i];
        if (level >= scroll.minLevel && level <= scroll.maxLevel) {
            return i;
        }
    }
    return -1;
}

// 시작 골드 (넉넉하게 시작)
export const STARTING_GOLD = 200000;

// 색상 (전설의 대장간 테마)
export const COLORS = {
    background: 0x1a1a1d,
    panel: 0x2c3e50,
    panelLight: 0x34495e,
    gold: 0xf1c40f,
    success: 0x2ecc71,
    fail: 0xe74c3c,
    text: 0xecf0f1,
    textDim: 0x95a5a6,
    primary: 0xd35400,
    secondary: 0x7f8c8d,
    accent: 0x9b59b6,
    frameBorder: 0x95a5a6,
    glow: 0xe67e22,
};
